import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState('register');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    password: '',
    customer_type: 'retail',
    company_name: '',
    address: '',
    delivery_point: ''
  });
  const [otp, setOtp] = useState('');
  const [otpHint, setOtpHint] = useState('');
  const [resending, setResending] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/customer/register`, formData);
      const sent = response.data.message || 'Registration successful! Please verify the code we sent you.';
      toast.success(sent);
      setOtpHint(sent);
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      const response = await axios.post(`${API}/customer/resend-otp`, { email: formData.email });
      const sent = response.data.message || 'A new code has been sent';
      toast.success(sent);
      setOtpHint(sent);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend the code');
    } finally {
      setResending(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/customer/verify-otp`, { email: formData.email, otp });
      toast.success('Account verified! Logging you in...');
      localStorage.setItem('customer_token', response.data.access_token);
      localStorage.setItem('customer_user', JSON.stringify(response.data.user));
      setTimeout(() => navigate('/customer/products'), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <Card className="w-full max-w-md border-2 border-[#8FC043]" data-testid="customer-register-card">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img src="/himan-logo.png" alt="Himan Logo" className="h-20 w-20 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Create Customer Account</CardTitle>
          <CardDescription className="text-center">
            {step === 'register' ? 'Register to start shopping' : 'Enter the OTP sent to your email'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'register' ? (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_type">Account Type *</Label>
                <Select value={formData.customer_type} onValueChange={(value) => setFormData({ ...formData, customer_type: value })}>
                  <SelectTrigger data-testid="customer-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retail">Retail Customer</SelectItem>
                    <SelectItem value="wholesale">Wholesale Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name (Optional)</Label>
                <Input
                  id="company_name"
                  data-testid="company-name-input"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Delivery Address (Optional)</Label>
                <Input
                  id="address"
                  data-testid="address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery_point">Delivery Point / Region (Optional)</Label>
                <Input
                  id="delivery_point"
                  data-testid="delivery-point-input"
                  placeholder="e.g. Kariakoo, Ilala, Kigoma branch"
                  value={formData.delivery_point}
                  onChange={(e) => setFormData({ ...formData, delivery_point: e.target.value })}
                />
              </div>
              <Button data-testid="register-submit-button" type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : 'Register'}
              </Button>
              <div className="text-center text-sm">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/customer/login')}
                  className="text-[#8FC043] hover:underline font-semibold"
                >
                  Login here
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">OTP Code</Label>
                <Input
                  id="otp"
                  data-testid="otp-input"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  required
                />
                {otpHint && (
                  <p className="text-sm text-gray-500">{otpHint}</p>
                )}
              </div>
              <Button data-testid="verify-otp-button" type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : 'Verify & Login'}
              </Button>
              <button
                type="button"
                data-testid="resend-otp-link"
                className="w-full text-sm text-gray-500 hover:text-[#8FC043] underline"
                onClick={handleResendOtp}
                disabled={resending}
              >
                {resending ? 'Sending…' : "Didn't get the code? Resend"}
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
